import React, { useState, useEffect } from "react";
import {
  getSettings,
  updateSettings,
  updateReglementHeaderImage,
  updateReglementFirstImage,
  updateReglementSecondImage,
} from "../../../services/settingsServices";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const ReglementInterieurForm = () => {
  const [settings, setSettings] = useState({
    reglement_interieur: "",
    reglement_interieur_url_header: "",
    reglement_interieur_url_firstimage: "",
    reglement_interieur_url_secondimage: "",
  });

  const [loading, setLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);

  // États pour gérer chaque fichier uploadé
  const [selectedHeader, setSelectedHeader] = useState(null);
  const [selectedFirst, setSelectedFirst] = useState(null);
  const [selectedSecond, setSelectedSecond] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsData = await getSettings();
        if (settingsData.length > 0) {
          setSettings(settingsData[0]);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des données:", error);
      }
      setLoading(false);
    };

    fetchSettings();
  }, []);

  const handleContentChange = (name, value) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateSettings(settings);
      toast.success("Paramètres mis à jour avec succès");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour des paramètres");
    }
  };

  /** HANDLERS D'UPLOAD INDIVIDUELS **/
  const handleUploadHeader = async () => {
    if (!selectedHeader) {
      toast.error("Veuillez sélectionner une image.");
      return;
    }

    setIsUploading(true);
    try {
      const token = localStorage.getItem("token");
      const imageUrl = await updateReglementHeaderImage(selectedHeader, token);
      if (imageUrl) {
        setSettings((prevSettings) => ({
          ...prevSettings,
          reglement_interieur_url_header: `${imageUrl}?t=${Date.now()}`,
        }));
        toast.success("Image d'en-tête mise à jour !");
      }
    } catch (error) {
      toast.error("Erreur lors de l'upload.");
    } finally {
      setSelectedHeader(null);
      setIsUploading(false);
    }
  };

  const handleUploadFirstImage = async () => {
    if (!selectedFirst) {
      toast.error("Veuillez sélectionner une image.");
      return;
    }

    setIsUploading(true);
    try {
      const token = localStorage.getItem("token");
      const imageUrl = await updateReglementFirstImage(selectedFirst, token);
      if (imageUrl) {
        setSettings((prevSettings) => ({
          ...prevSettings,
          reglement_interieur_url_firstimage: `${imageUrl}?t=${Date.now()}`,
        }));
        toast.success("Première image mise à jour !");
      }
    } catch (error) {
      toast.error("Erreur lors de l'upload.");
    } finally {
      setSelectedFirst(null);
      setIsUploading(false);
    }
  };

  const handleUploadSecondImage = async () => {
    if (!selectedSecond) {
      toast.error("Veuillez sélectionner une image.");
      return;
    }

    setIsUploading(true);
    try {
      const token = localStorage.getItem("token");
      const imageUrl = await updateReglementSecondImage(selectedSecond, token);
      if (imageUrl) {
        setSettings((prevSettings) => ({
          ...prevSettings,
          reglement_interieur_url_secondimage: `${imageUrl}?t=${Date.now()}`,
        }));
        toast.success("Deuxième image mise à jour !");
      }
    } catch (error) {
      toast.error("Erreur lors de l'upload.");
    } finally {
      setSelectedSecond(null);
      setIsUploading(false);
    }
  };

  if (loading) return <div>Chargement...</div>;

  return (
    <div className="bg-white dark:bg-slate-300 shadow-md rounded-lg p-4 max-w-4xl">
      <h3 className="text-xl font-semibold mb-6">Règlement Intérieur</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex flex-col">
          <label className="text-sm font-medium text-gray-700 mb-1">
            Texte du règlement intérieur
          </label>
          <ReactQuill
            value={settings.reglement_interieur}
            onChange={(value) =>
              handleContentChange("reglement_interieur", value)
            }
            className="mb-3 bg-slate-200"
          />
        </div>

        {/* Carte pour l'image d'en-tête */}
        <ImageUploadCard
          title="Image d'en-tête"
          currentImage={settings.reglement_interieur_url_header}
          selectedImage={selectedHeader}
          setSelectedImage={setSelectedHeader}
          handleUpload={handleUploadHeader}
        />

        {/* Carte pour la première image */}
        <ImageUploadCard
          title="Première image"
          currentImage={settings.reglement_interieur_url_firstimage}
          selectedImage={selectedFirst}
          setSelectedImage={setSelectedFirst}
          handleUpload={handleUploadFirstImage}
        />

        {/* Carte pour la deuxième image */}
        <ImageUploadCard
          title="Deuxième image"
          currentImage={settings.reglement_interieur_url_secondimage}
          selectedImage={selectedSecond}
          setSelectedImage={setSelectedSecond}
          handleUpload={handleUploadSecondImage}
        />

        <div className="flex justify-end">
          <Button
            text="Mettre à jour"
            type="submit"
            className="btn-primary gap-2"
          />
        </div>
      </form>
    </div>
  );
};

/** Composant Réutilisable pour la Gestion des Images */
const ImageUploadCard = ({
  title,
  currentImage,
  selectedImage,
  setSelectedImage,
  handleUpload,
}) => {
  return (
    <div className="bg-gray-100 shadow-md rounded-lg p-4 flex items-center justify-between mb-4">
      {/* Image à gauche */}
      <div className="w-32 h-32 flex-shrink-0 border rounded-md overflow-hidden">
        {selectedImage ? (
          <img
            src={URL.createObjectURL(selectedImage)}
            alt="Aperçu"
            className="w-full h-full object-cover"
          />
        ) : (
          <img
            src={currentImage}
            alt="Actuelle"
            className="w-full h-full object-cover"
          />
        )}
      </div>

      {/* Choix fichier et bouton à droite */}
      <div className="flex flex-col items-start space-y-2 w-full ml-4">
        <label className="text-sm font-medium text-gray-700">{title}</label>
        <input
          type="file"
          onChange={(e) => setSelectedImage(e.target.files[0])}
          accept="image/*"
          className="w-full text-sm"
        />
        {selectedImage && (
          <Button
            text="Sauvegarder"
            onClick={handleUpload}
            className="mt-2 btn-primary self-start"
          />
        )}
      </div>
    </div>
  );
};

export default ReglementInterieurForm;
